import { SIMA, STRATUM } from '@somnhold/content';
import { simaFloor, simaOpen, simaRecord, simaStarts, simaWeekSeed, simaOrder, type CampaignState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import type { CampaignService } from './service.ts';

/*
 * The Chasm's card (docs/SIMA.md, «La tarjeta»): Hilde's three lines, the group's mark ever and this week, who went
 * down, the floors a run can start from and what the week holds below each (stratum, guardian, signs), and the way
 * down. Plain DOM; the colours of the strata are the ones the floors are lit with.
 */

/** How many floors the card shows below the chosen start. */
const AHEAD = 6;

/** Days left in the Chasm's week. */
const daysLeft = (day: number): number => SIMA.weekDays - (day % SIMA.weekDays);

const nameOf = (s: CampaignState, pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';

/** The floors below `start` this week: a row each, with its stratum's colour, its guardian and its signs. */
const ahead = (s: CampaignState, day: number, start: number): HTMLElement => {
  const seed = simaWeekSeed(s, simaRecord(s, day).week);
  const order = simaOrder(seed);
  const rows: HTMLElement[] = [];
  for (let f = start; f < start + AHEAD; f++) {
    const i = simaFloor(seed, order, f);
    const signs = i.signs.map((g) =>
      h(
        'span.sima-sign',
        {
          title: t(`sima.signText.${g}`, {
            type: i.ward ? t(`dtype.${i.ward}`) : '',
          }),
        },
        t(`sima.sign.${g}`, { type: i.ward ? t(`dtype.${i.ward}`) : '' }),
      ),
    );
    rows.push(
      h(
        `li.sima-floor${i.threshold ? '.guardian' : ''}`,
        {
          style: `--stratum:${STRATUM[i.stratum].color}`,
          'data-test': `sima-floor-${f}`,
        },
        h('span.sima-n', {}, String(f)),
        h('span.sima-dot'),
        h('span.sima-name', {}, t(`sima.stratum.${i.stratum}`)),
        i.threshold ? h('span.sima-guard', {}, h('span', { html: ICONS.skull }), t(`sima.guardian.${STRATUM[i.stratum].guardian}`)) : '',
        signs.length ? h('span.sima-signs', {}, ...signs) : '',
      ),
    );
  }
  return h('ol.sima-ahead', { 'data-test': 'sima-ahead' }, ...rows);
};

export const simaCard = (svc: CampaignService, onSima: (start: number) => void): HTMLElement | null => {
  const s = svc.state;
  const open = simaOpen(s);
  const day = svc.day();
  const rec = simaRecord(s, day);
  const me = s.players.find((p) => p.id === svc.me);
  const starts = open ? simaStarts(s) : [1];
  let start = starts[starts.length - 1] ?? 1;
  const room = svc.presence()?.rooms.find((r) => r.id.startsWith('sima-') && !r.started);
  const left = daysLeft(day);
  const facts = h(
    'ul.crypt-facts',
    {},
    h('li', { 'data-test': 'sima-mark' }, h('span', { html: ICONS.skull }), rec.best > 0 ? t('sima.factMark', { n: rec.best }) : t('sima.factNoMark')),
    h('li', { 'data-test': 'sima-week' }, h('span', { html: ICONS.hourglass }), rec.weekBest === 0 ? t('sima.factWeekNone', { d: left }) : left <= 1 ? t('sima.factWeekOne', { n: rec.weekBest }) : t('sima.factWeek', { n: rec.weekBest, d: left })),
    rec.went.length
      ? h(
          'li',
          {},
          h('span', { html: ICONS.hero }),
          t('sima.factWent', {
            names: rec.went.map((p) => nameOf(s, p)).join(', '),
          }),
        )
      : '',
    room
      ? h(
          'li',
          {},
          h('span', { html: ICONS.globe }),
          t('sima.factGathering', {
            names: room.players.map((p) => nameOf(s, p)).join(', ') || '—',
          }),
        )
      : '',
    h('li', {}, h('span', { html: ICONS.chest }), me?.simaDay === day ? t('sima.factPiece') : t('sima.factPieceTodo')),
    h('li', {}, h('span', { html: ICONS.hero }), t('sima.factParty', { max: SIMA.maxHeroes })),
  );
  const list = h('div.sima-ahead-box', {}, h('b.sima-sub', {}, t('sima.next')), ahead(s, day, start));
  const pick = (n: number): void => {
    start = n;
    for (const b of chips.querySelectorAll('button')) b.classList.toggle('on', Number(b.dataset.start) === n);
    list.replaceChildren(h('b.sima-sub', {}, t('sima.next')), ahead(s, day, start));
  };
  const chips = h(
    'div.sima-starts',
    { role: 'group', 'aria-label': t('sima.start') },
    ...starts.map((n) =>
      h(
        `button.chip.sima-start${n === start ? '.on' : ''}`,
        {
          type: 'button',
          'data-start': String(n),
          'data-test': `sima-start-${n}`,
          onclick: () => pick(n),
        },
        t('sima.startFloor', { n }),
      ),
    ),
  );
  const scene = h(
    'blockquote.sima-scene',
    {},
    h('p', {}, h('b', {}, 'Hilde: '), t('sima.scene1')),
    h('p', {}, h('b', {}, 'Hilde: '), t('sima.scene2')),
    h('p', {}, h('b', {}, 'Brann: '), t('sima.scene3')),
  );
  const go = h('button.btn.crypt-go.sima-go', { 'data-test': 'sima-go', disabled: !open, onclick: () => onSima(start) }, t('sima.go'));
  return h(
    `section.crypt.sima-card${open ? '.open' : ''}`,
    { 'data-test': 'sima-card' },
    h('div.crypt-art', { html: SIMA_SVG }),
    h(
      'div.crypt-body',
      {},
      h('span.label', {}, t('sima.label')),
      h('h3', {}, t('sima.title')),
      h('p', {}, t('sima.pitch')),
      scene,
      open ? facts : h('p.crypt-lock', {}, h('span', { html: ICONS.lock }), t('sima.lock')),
      open && starts.length > 1 ? h('div.sima-start-box', {}, h('b.sima-sub', {}, t('sima.start')), chips) : '',
      open ? list : '',
      go,
    ),
  );
};

/** A crack in the floor of a crypt, the rope of a winch going down into red dark. */
const SIMA_SVG = `<svg viewBox="0 0 120 120" aria-hidden="true"><defs><radialGradient id="sg" cx="50%" cy="70%" r="50%"><stop offset="0" stop-color="#ff4f3a" stop-opacity=".55"/><stop offset="1" stop-color="#ff4f3a" stop-opacity="0"/></radialGradient></defs>
<rect width="120" height="120" fill="#0b0c10"/><path d="M14 30h92M24 30v-10h72v10" stroke="#4a4c53" stroke-width="4" fill="none"/><circle cx="60" cy="22" r="6" fill="#2a2d33" stroke="#6b6f78" stroke-width="2"/>
<path d="M60 28v58" stroke="#8a8d96" stroke-width="1.6" stroke-dasharray="3 2"/><path d="M34 62l14 6-6 10 18 6-10 12 18 4" stroke="#3a3d45" stroke-width="3" fill="none"/>
<path d="M30 70c8 2 10 14 30 14s22-12 30-14c-4 22-14 40-30 40s-26-18-30-40z" fill="#07080a"/><ellipse cx="60" cy="92" rx="26" ry="16" fill="url(#sg)"/>
<g fill="#8fa3c0"><rect x="57" y="50" width="6" height="5" rx="1"/></g><g fill="#a77bff"><rect x="57" y="60" width="6" height="5" rx="1"/></g><g fill="#5fd0b8"><rect x="57" y="70" width="6" height="5" rx="1"/></g></svg>`;
